import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import { useParams } from 'react-router-dom';

import Button from '../../../components/Button';
import Form from '../../../components/Form';
import QuantityChanger from '../../../components/QuantityChanger';
import FormField from '../../../components/FormField';
import Divider from '../../../components/Divider';

import MenuAllergens from '../MenuAllergens';
import MenuModalSize from '../MenuModalSize';
import MenuModalModifiers from '../MenuModalModifiers';
import MenuPriorities from '../MenuPriorities';

import MenuDietaryPreferences from '../MenuDietaryPreferences';

import {
  buildInitialSelection,
  computeTotal,
  computeValidity,
  buildPayload,
  selectRadio,
  toggleCheckbox,
  setQuantity,
} from '../modifiers/modifierSelection';

import formatCurrency from '../../../utils/formatCurrency';
import { safeMultiply, safeSum } from '../../../utils/safeArithmetics';

import ItemFormValidationSchema from './ItemFormValidationSchema';

import cySelectors from '../../../tests/cySelectors';

const MenuModalForm = ({
  currentItem,
  priorities,
  handleOnSubmit,
  isViewOnly,
  hasAllergensSelected,
}) => {
  const { brandMenuId, menuId } = useParams();
  const [itemQuantity, setItemQuantity] = useState(1);
  const [addToCartTotal, setAddToCartTotal] = useState(0);
  const [sizeTotal, setSizeTotal] = useState(0);
  const [formInstance, setFormInstance] = useState({});
  const { isValid } = formInstance || {};
  const isPreviewMode = !!brandMenuId || !!menuId;

  const {
    itemName,
    itemLabel,
    description,
    itemPrices,
    itemOptions,
    modifiers,
    dietaryRequirements,
    itemCalories,
    discount,
    priorityId,
  } = currentItem;

  const [selection, setSelection] = useState(() => buildInitialSelection(modifiers || []));
  const modifiersSum = computeTotal(modifiers || [], selection);
  const areModifiersValid = computeValidity(modifiers || [], selection);

  const handleSelectRadio = useCallback((groupKey, itemId) => {
    setSelection((prevState) => selectRadio(prevState, groupKey, itemId));
  }, []);

  const handleToggleCheckbox = useCallback((groupKey, itemId) => {
    setSelection((prevState) => toggleCheckbox(prevState, groupKey, itemId));
  }, []);

  const handleSetModifierQuantity = useCallback((groupKey, itemId, quantity) => {
    setSelection((prevState) => setQuantity(prevState, groupKey, itemId, quantity));
  }, []);

  const { loading: orderLoading } = useSelector((state) => state.order);
  const dietaryPreferences = dietaryRequirements?.filter(({ type }) => type === 'requirement');
  const allergens = dietaryRequirements?.filter(({ type }) => type === 'allergen');
  const hasAllergens = !!allergens?.length;
  const hasDietaryPreferences = !!dietaryPreferences?.length;
  const hasDietaryRequirements = hasAllergens || hasDietaryPreferences;
  const hasItemCalories = !!itemCalories?.length && itemCalories?.[0];
  const formattedAddToCartTotal = formatCurrency(addToCartTotal);
  const [itemPrice] = itemPrices;
  const discountValue = discount?.discountAmount;
  const discountedAmount = safeMultiply(sizeTotal / 100, discountValue);
  const discountTotal = safeMultiply(discountedAmount, itemQuantity);
  const formattedFinalPrice = formatCurrency(itemPrice - discountedAmount || itemPrice);
  const formattedDiscountedAddToCartTotal = formatCurrency(addToCartTotal - discountTotal);
  const classes = clsx({
    'item__modal--has-discount': discount,
  });

  const handleSubmit = (values) => {
    const updatedValues = { ...values, modifiers: buildPayload(modifiers || [], selection) };
    handleOnSubmit(itemQuantity, updatedValues);
  };

  const handleSetSizeTotal = useCallback((itemOptionPrice) => {
    setSizeTotal(itemOptionPrice);
  }, []);

  const increaseQuantity = () => {
    setItemQuantity(itemQuantity + 1);
  };

  const decreaseQuantity = () => {
    setItemQuantity((prevState) => (prevState <= 1 ? 1 : itemQuantity - 1));
  };

  const calculateAddToCartTotal = useCallback(
    () => safeMultiply(safeSum(modifiersSum, sizeTotal), itemQuantity),
    [sizeTotal, modifiersSum, itemQuantity],
  );

  useEffect(() => {
    setAddToCartTotal(calculateAddToCartTotal());
  }, [calculateAddToCartTotal]);

  return (
    <Form
      handleOnSubmit={handleSubmit}
      setFormInstance={setFormInstance}
      extraClasses={classes}
      disabled={isViewOnly || hasAllergensSelected}
      validationSchema={ItemFormValidationSchema}
      mode="onChange"
    >
      <div className="item__modal__body">
        {hasItemCalories && (
          <p className="item__modal__body__calories" data-cy={cySelectors.MENU_ITEMS_KCALS}>
            {itemCalories[0]} kcal
          </p>
        )}
        <div className="item__modal__body__title">
          <span className="item__modal__body__title__name">{itemLabel || itemName}</span>
          <span className="item__modal__body__title__price" data-cy={cySelectors.MENU_ITEMS_PRICE}>
            {formattedFinalPrice}
          </span>
        </div>
        {description && typeof description === 'string' && (
          <p className="item__modal__body__description">{description}</p>
        )}
        {itemOptions && itemOptions.length === 1 && (
          <p className="item__modal__body__single_size">{itemOptions[0]}</p>
        )}
        <Divider />
        {hasDietaryRequirements && (
          <>
            {hasDietaryPreferences && (
              <MenuDietaryPreferences dietaryPreferences={dietaryPreferences} />
            )}
            {hasAllergens && <MenuAllergens allergens={allergens} />}
            <Divider />
          </>
        )}
        {priorityId && (
          <>
            <MenuPriorities
              priorities={priorities}
              assignedPriorityId={priorityId}
              isViewOnly={isViewOnly}
            />
            <Divider />
          </>
        )}
        {itemPrices && itemOptions && (
          <MenuModalSize
            itemPrices={itemPrices}
            itemOptions={itemOptions}
            discount={discount}
            handleSetSizeTotal={handleSetSizeTotal}
          />
        )}
        {modifiers && (
          <>
            <MenuModalModifiers
              modifiers={modifiers}
              selection={selection}
              handleSelectRadio={handleSelectRadio}
              handleToggleCheckbox={handleToggleCheckbox}
              handleSetQuantity={handleSetModifierQuantity}
            />
            <Divider />
          </>
        )}
        {!isViewOnly && (
          <FormField
            type="textarea"
            name="notes"
            label="Additional requests"
            maxLength={51}
            disabled={isViewOnly}
            data-cy={cySelectors.MENU_ITEMS_MODAL_NOTES}
          />
        )}
      </div>
      {!isViewOnly && !hasAllergensSelected && (
        <footer className="item__modal__footer" data-cy={cySelectors.MENU_ITEMS_MODAL_FOOTER}>
          <QuantityChanger
            quantity={itemQuantity}
            increaseQuantity={increaseQuantity}
            decreaseQuantity={decreaseQuantity}
          />
          <div className="item__modal__footer__btn">
            <Button
              fullWidth
              type="submit"
              disabled={
                orderLoading ||
                !areModifiersValid ||
                !isValid ||
                isPreviewMode
              }
              loading={orderLoading}
              data-cy={cySelectors.MODIFIERS_SUBMIT_BTN}
            >
              <span>{isPreviewMode ? 'Basket amount' : 'Add to order'}</span>
              <div className="item__modal__footer__btn__total">
                <span
                  data-cy={cySelectors.MENU_ITEMS_MODAL_TOTAL}
                  className="item__modal__footer__btn__total__amount"
                >
                  {formattedAddToCartTotal}
                </span>
                {discount && (
                  <span
                    data-cy={cySelectors.MENU_ITEMS_MODAL_TOTAL_DISCOUNTED}
                    className="item__modal__footer__btn__total__discounted"
                  >
                    {formattedDiscountedAddToCartTotal}
                  </span>
                )}
              </div>
            </Button>
          </div>
        </footer>
      )}
      {hasAllergensSelected && (
        <footer className="item__modal__footer" data-cy={cySelectors.MENU_ITEMS_MODAL_FOOTER}>
          <p>Remove allergen filter to order</p>
        </footer>
      )}
    </Form>
  );
};

export default MenuModalForm;
